import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { adminApi } from '../lib/api';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { ScrollArea } from '../components/ui/scroll-area';
import { Textarea } from '../components/ui/textarea';
import { Switch } from '../components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { 
  Users, FileText, BarChart3, ClipboardList, Shield, 
  Search, Crown, Clock, TrendingUp, ArrowLeft, BookOpen,
  Plus, Edit, Trash2, Settings, Loader2, Building2
} from 'lucide-react';
import { toast } from 'sonner';
import MarketplaceAdmin from '../components/MarketplaceAdmin';

export default function AdminPage() {
  const { user, isAdmin } = useAuth();
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [auditLogs, setAuditLogs] = useState([]);
  const [articles, setArticles] = useState([]);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  
  // Article dialog state
  const [articleDialogOpen, setArticleDialogOpen] = useState(false);
  const [editingArticle, setEditingArticle] = useState(null);
  const [articleForm, setArticleForm] = useState({
    title: '',
    slug: '',
    category: 'Credit Basics',
    excerpt: '',
    content: '',
    featured_image: '',
    published: false
  });
  const [savingArticle, setSavingArticle] = useState(false);

  const categories = ['Credit Basics', 'Dispute Tips', 'Score Improvement', 'Debt Management', 'Credit Cards', 'Loans', 'Identity Theft'];

  useEffect(function checkAdminAndLoad() {
    if (!isAdmin) {
      navigate('/dashboard');
      return;
    }
    fetchData();
  }, [isAdmin, navigate]);

  async function fetchData() {
    try {
      const usersData = await adminApi.getUsers();
      setUsers(usersData.users || []);
    } catch (err) {
      console.error('Failed to fetch users:', err);
    }
    try {
      const templatesData = await adminApi.getTemplates();
      setTemplates(templatesData.templates || []);
    } catch (err) {
      console.error('Failed to fetch templates:', err);
    }
    try {
      const analyticsData = await adminApi.getAnalytics();
      setAnalytics(analyticsData);
    } catch (err) {
      console.error('Failed to fetch analytics:', err);
    }
    try {
      const logsData = await adminApi.getAuditLogs();
      setAuditLogs(logsData.logs || []);
    } catch (err) {
      console.error('Failed to fetch logs:', err);
    }
    try {
      const articlesData = await adminApi.getArticles();
      setArticles(articlesData.articles || []);
    } catch (err) {
      console.error('Failed to fetch articles:', err);
    }
    try {
      const settingsData = await adminApi.getSettings();
      setSettings(settingsData);
    } catch (err) {
      console.error('Failed to fetch settings:', err);
    }
    setLoading(false);
  }

  const handleRoleChange = useCallback(async function(userId, newRole) {
    try {
      await adminApi.updateUserRole(userId, newRole);
      toast.success('User role updated');
      fetchData();
    } catch (err) {
      toast.error('Failed to update role');
    }
  }, []);

  function generateSlug(title) {
    return title
      .toLowerCase()
      .replace(/[^a-z0-9\s-]/g, '')
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-')
      .trim();
  }

  function handleTitleChange(e) {
    const title = e.target.value;
    setArticleForm(function(prev) {
      return {
        ...prev,
        title: title,
        slug: editingArticle ? prev.slug : generateSlug(title)
      };
    });
  }

  function openNewArticleDialog() {
    setEditingArticle(null);
    setArticleForm({
      title: '',
      slug: '',
      category: 'Credit Basics',
      excerpt: '',
      content: '',
      featured_image: '',
      published: false
    });
    setArticleDialogOpen(true);
  }

  function openEditArticleDialog(article) {
    setEditingArticle(article);
    setArticleForm({
      title: article.title,
      slug: article.slug,
      category: article.category,
      excerpt: article.excerpt,
      content: article.content,
      featured_image: article.featured_image || '',
      published: article.published
    });
    setArticleDialogOpen(true);
  }

  async function handleSaveArticle() {
    if (!articleForm.title || !articleForm.slug || !articleForm.content) {
      toast.error('Please fill in title, slug, and content');
      return;
    }
    
    setSavingArticle(true);
    try {
      if (editingArticle) {
        await adminApi.updateArticle(editingArticle.article_id, articleForm);
        toast.success('Article updated');
      } else {
        await adminApi.createArticle(articleForm);
        toast.success('Article created');
      }
      setArticleDialogOpen(false);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save article');
    } finally {
      setSavingArticle(false);
    }
  }

  async function handleDeleteArticle(articleId) {
    if (!window.confirm('Are you sure you want to delete this article?')) return;
    
    try {
      await adminApi.deleteArticle(articleId);
      toast.success('Article deleted');
      fetchData();
    } catch (err) {
      toast.error('Failed to delete article');
    }
  }

  async function handleSaveSettings() {
    try {
      await adminApi.updateSettings({
        disputes_per_month: settings.disputes_per_month,
        dispute_cycle_days: settings.dispute_cycle_days,
        max_items_per_dispute: settings.max_items_per_dispute
      });
      toast.success('Settings saved');
    } catch (err) {
      toast.error('Failed to save settings');
    }
  }

  const filteredUsers = users.filter(function(u) {
    const search = searchTerm.toLowerCase();
    const email = u.email ? u.email.toLowerCase() : '';
    const firstName = u.first_name ? u.first_name.toLowerCase() : '';
    const lastName = u.last_name ? u.last_name.toLowerCase() : '';
    return email.includes(search) || firstName.includes(search) || lastName.includes(search);
  });

  if (!isAdmin) return null;

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 p-8">
        <div className="animate-pulse space-y-6 max-w-7xl mx-auto">
          <div className="h-8 bg-slate-200 rounded w-1/4" />
          <div className="grid grid-cols-4 gap-6">
            <div className="h-24 bg-slate-200 rounded-xl" />
            <div className="h-24 bg-slate-200 rounded-xl" />
            <div className="h-24 bg-slate-200 rounded-xl" />
            <div className="h-24 bg-slate-200 rounded-xl" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50" data-testid="admin-page">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 px-4 md:px-8 py-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button 
              variant="ghost" 
              size="icon"
              onClick={function() { navigate('/dashboard'); }}
              data-testid="back-btn"
            >
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div className="flex items-center gap-2">
              <Shield className="h-6 w-6 text-teal-600" />
              <span className="font-bold text-xl text-slate-900" style={{ fontFamily: 'Manrope' }}>
                Admin Panel
              </span>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Badge className="bg-purple-100 text-purple-700">Admin</Badge>
            <span className="text-sm text-slate-600">{user ? user.email : ''}</span>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto p-4 md:p-8 space-y-6">
        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card className="border-slate-100">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Total Users</p>
                  <p className="text-2xl font-bold text-slate-900">{analytics ? analytics.total_users : 0}</p>
                </div>
                <Users className="h-8 w-8 text-slate-300" />
              </div>
            </CardContent>
          </Card>
          <Card className="border-slate-100">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Premium Users</p>
                  <p className="text-2xl font-bold text-teal-600">{analytics ? analytics.premium_users : 0}</p>
                </div>
                <Crown className="h-8 w-8 text-teal-200" />
              </div>
            </CardContent>
          </Card>
          <Card className="border-slate-100">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Total Disputes</p>
                  <p className="text-2xl font-bold text-slate-900">{analytics ? analytics.total_disputes : 0}</p>
                </div>
                <FileText className="h-8 w-8 text-slate-300" />
              </div>
            </CardContent>
          </Card>
          <Card className="border-slate-100">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Articles</p>
                  <p className="text-2xl font-bold text-blue-600">{articles.length}</p>
                </div>
                <BookOpen className="h-8 w-8 text-blue-200" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Tabs */}
        <Tabs defaultValue="users" className="space-y-4">
          <TabsList data-testid="admin-tabs">
            <TabsTrigger value="users">
              <Users className="h-4 w-4 mr-2" />
              Users
            </TabsTrigger>
            <TabsTrigger value="marketplace">
              <Building2 className="h-4 w-4 mr-2" />
              Marketplace
            </TabsTrigger>
            <TabsTrigger value="articles">
              <BookOpen className="h-4 w-4 mr-2" />
              Articles
            </TabsTrigger>
            <TabsTrigger value="settings">
              <Settings className="h-4 w-4 mr-2" />
              Settings
            </TabsTrigger>
            <TabsTrigger value="analytics">
              <BarChart3 className="h-4 w-4 mr-2" />
              Analytics
            </TabsTrigger>
            <TabsTrigger value="audit">
              <ClipboardList className="h-4 w-4 mr-2" />
              Audit Logs
            </TabsTrigger>
          </TabsList>

          {/* Marketplace Tab */}
          <TabsContent value="marketplace">
            <Card className="border-slate-100">
              <CardHeader>
                <CardTitle style={{ fontFamily: 'Manrope' }}>Marketplace Management</CardTitle>
                <CardDescription>Manage affiliate partners and providers</CardDescription>
              </CardHeader>
              <CardContent>
                <MarketplaceAdmin />
              </CardContent>
            </Card>
          </TabsContent>

          {/* Users Tab */}
          <TabsContent value="users">
            <Card className="border-slate-100">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle style={{ fontFamily: 'Manrope' }}>User Management</CardTitle>
                    <CardDescription>{users.length} total users</CardDescription>
                  </div>
                  <div className="relative w-64">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                    <Input
                      placeholder="Search users..."
                      value={searchTerm}
                      onChange={function(e) { setSearchTerm(e.target.value); }}
                      className="pl-9"
                      data-testid="search-users"
                    />
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <ScrollArea className="h-[400px]">
                  <div className="space-y-2">
                    {filteredUsers.map(function(u) {
                      return (
                        <div 
                          key={u.user_id}
                          className="flex items-center justify-between p-4 bg-slate-50 rounded-xl"
                          data-testid={'user-row-' + u.user_id}
                        >
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-full bg-teal-100 flex items-center justify-center text-teal-700 font-semibold">
                              {u.first_name ? u.first_name[0] : ''}{u.last_name ? u.last_name[0] : ''}
                            </div>
                            <div>
                              <p className="font-medium text-slate-900">
                                {u.first_name} {u.last_name}
                              </p>
                              <p className="text-sm text-slate-500">{u.email}</p>
                            </div>
                          </div>
                          <div className="flex items-center gap-3">
                            <Badge className={u.subscription_plan === 'premium' ? 'bg-teal-100 text-teal-700' : 'bg-slate-200 text-slate-700'}>
                              {u.subscription_plan}
                            </Badge>
                            <Select 
                              value={u.role}
                              onValueChange={function(value) { handleRoleChange(u.user_id, value); }}
                            >
                              <SelectTrigger className="w-28">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="user">User</SelectItem>
                                <SelectItem value="admin">Admin</SelectItem>
                                <SelectItem value="support">Support</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </ScrollArea>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Articles Tab */}
          <TabsContent value="articles">
            <Card className="border-slate-100">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle style={{ fontFamily: 'Manrope' }}>Article Management</CardTitle>
                    <CardDescription>Create and manage blog articles</CardDescription>
                  </div>
                  <Button onClick={openNewArticleDialog} className="btn-teal gap-2" data-testid="new-article-btn">
                    <Plus className="h-4 w-4" />
                    New Article
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {articles.length === 0 ? (
                  <div className="text-center py-12">
                    <BookOpen className="h-12 w-12 mx-auto mb-4 text-slate-300" />
                    <p className="text-slate-600">No articles yet. Create your first article!</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {articles.map(function(article) {
                      return (
                        <div 
                          key={article.article_id}
                          className="flex items-center justify-between p-4 bg-slate-50 rounded-xl"
                          data-testid={'article-row-' + article.article_id}
                        >
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <p className="font-medium text-slate-900">{article.title}</p>
                              {article.published ? (
                                <Badge className="bg-green-100 text-green-700">Published</Badge>
                              ) : (
                                <Badge variant="outline">Draft</Badge>
                              )}
                            </div>
                            <p className="text-sm text-slate-500">
                              {article.category} • {new Date(article.created_at).toLocaleDateString()}
                            </p>
                          </div>
                          <div className="flex items-center gap-2">
                            <Button 
                              variant="ghost" 
                              size="icon"
                              onClick={function() { openEditArticleDialog(article); }}
                              data-testid={'edit-article-' + article.article_id}
                            >
                              <Edit className="h-4 w-4 text-slate-500" />
                            </Button>
                            <Button 
                              variant="ghost" 
                              size="icon"
                              onClick={function() { handleDeleteArticle(article.article_id); }}
                              data-testid={'delete-article-' + article.article_id}
                            >
                              <Trash2 className="h-4 w-4 text-red-500" />
                            </Button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Settings Tab */}
          <TabsContent value="settings">
            <Card className="border-slate-100">
              <CardHeader>
                <CardTitle style={{ fontFamily: 'Manrope' }}>Platform Settings</CardTitle>
                <CardDescription>Configure dispute limits and automation rules</CardDescription>
              </CardHeader>
              <CardContent>
                {settings ? (
                  <div className="space-y-6 max-w-md">
                    <div className="space-y-2">
                      <Label htmlFor="disputes_per_month">Disputes Per Month (Premium)</Label>
                      <Input
                        id="disputes_per_month"
                        type="number"
                        value={settings.disputes_per_month || 5}
                        onChange={function(e) {
                          setSettings(function(prev) {
                            return { ...prev, disputes_per_month: parseInt(e.target.value) || 5 };
                          });
                        }}
                        className="w-32"
                        data-testid="disputes-per-month"
                      />
                      <p className="text-xs text-slate-500">Maximum dispute letters per month for premium users</p>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="dispute_cycle_days">Dispute Cycle (Days)</Label>
                      <Input
                        id="dispute_cycle_days"
                        type="number"
                        value={settings.dispute_cycle_days || 30}
                        onChange={function(e) {
                          setSettings(function(prev) {
                            return { ...prev, dispute_cycle_days: parseInt(e.target.value) || 30 };
                          });
                        }}
                        className="w-32"
                        data-testid="dispute-cycle-days"
                      />
                      <p className="text-xs text-slate-500">Days between automatic dispute cycles</p>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="max_items_per_dispute">Max Items Per Dispute Letter</Label>
                      <Input
                        id="max_items_per_dispute"
                        type="number"
                        value={settings.max_items_per_dispute || 5}
                        onChange={function(e) {
                          setSettings(function(prev) {
                            return { ...prev, max_items_per_dispute: parseInt(e.target.value) || 5 };
                          });
                        }}
                        className="w-32"
                        data-testid="max-items-per-dispute"
                      />
                      <p className="text-xs text-slate-500">Maximum items to include in a single dispute letter</p>
                    </div>
                    <Button onClick={handleSaveSettings} className="btn-teal" data-testid="save-settings-btn">
                      Save Settings
                    </Button>
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <Loader2 className="h-8 w-8 mx-auto animate-spin text-slate-300" />
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Analytics Tab */}
          <TabsContent value="analytics">
            <Card className="border-slate-100">
              <CardHeader>
                <CardTitle style={{ fontFamily: 'Manrope' }}>Analytics Overview</CardTitle>
                <CardDescription>Platform performance metrics</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-4">
                    <h3 className="font-medium text-slate-900">User Stats</h3>
                    <div className="space-y-2">
                      <div className="flex justify-between p-3 bg-slate-50 rounded-lg">
                        <span className="text-slate-600">Total Users</span>
                        <span className="font-medium">{analytics ? analytics.total_users : 0}</span>
                      </div>
                      <div className="flex justify-between p-3 bg-slate-50 rounded-lg">
                        <span className="text-slate-600">Free Users</span>
                        <span className="font-medium">{analytics ? analytics.free_users : 0}</span>
                      </div>
                      <div className="flex justify-between p-3 bg-slate-50 rounded-lg">
                        <span className="text-slate-600">Premium Users</span>
                        <span className="font-medium">{analytics ? analytics.premium_users : 0}</span>
                      </div>
                      <div className="flex justify-between p-3 bg-teal-50 rounded-lg">
                        <span className="text-teal-700">Conversion Rate</span>
                        <span className="font-medium text-teal-700">
                          {analytics && analytics.conversion_rate ? analytics.conversion_rate.toFixed(1) : 0}%
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className="space-y-4">
                    <h3 className="font-medium text-slate-900">Dispute Stats</h3>
                    <div className="space-y-2">
                      <div className="flex justify-between p-3 bg-slate-50 rounded-lg">
                        <span className="text-slate-600">Total Disputes</span>
                        <span className="font-medium">{analytics ? analytics.total_disputes : 0}</span>
                      </div>
                      <div className="flex justify-between p-3 bg-slate-50 rounded-lg">
                        <span className="text-slate-600">Resolved Disputes</span>
                        <span className="font-medium">{analytics ? analytics.resolved_disputes : 0}</span>
                      </div>
                      <div className="flex justify-between p-3 bg-green-50 rounded-lg">
                        <span className="text-green-700">Success Rate</span>
                        <span className="font-medium text-green-700">
                          {analytics && analytics.success_rate ? analytics.success_rate.toFixed(1) : 0}%
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Audit Logs Tab */}
          <TabsContent value="audit">
            <Card className="border-slate-100">
              <CardHeader>
                <CardTitle style={{ fontFamily: 'Manrope' }}>Audit Logs</CardTitle>
                <CardDescription>System activity and changes</CardDescription>
              </CardHeader>
              <CardContent>
                <ScrollArea className="h-[400px]">
                  <div className="space-y-2">
                    {auditLogs.length === 0 ? (
                      <p className="text-center text-slate-500 py-8">No audit logs yet</p>
                    ) : (
                      auditLogs.map(function(log) {
                        return (
                          <div 
                            key={log.log_id}
                            className="flex items-start gap-3 p-4 bg-slate-50 rounded-xl"
                          >
                            <Clock className="h-5 w-5 text-slate-400 mt-0.5" />
                            <div className="flex-1">
                              <div className="flex items-center gap-2">
                                <Badge variant="outline">{log.action}</Badge>
                                <span className="text-xs text-slate-500">
                                  {new Date(log.timestamp).toLocaleString()}
                                </span>
                              </div>
                              <p className="text-sm text-slate-600 mt-1">
                                User: {log.user_id ? log.user_id.slice(0, 8) + '...' : 'Unknown'}
                              </p>
                            </div>
                          </div>
                        );
                      })
                    )}
                  </div>
                </ScrollArea>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </main>

      {/* Article Dialog */}
      <Dialog open={articleDialogOpen} onOpenChange={setArticleDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="article-dialog">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Manrope' }}>
              {editingArticle ? 'Edit Article' : 'New Article'}
            </DialogTitle>
            <DialogDescription>
              {editingArticle ? 'Update article details' : 'Create a new blog article'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label>Title</Label>
              <Input
                value={articleForm.title}
                onChange={handleTitleChange}
                placeholder="Article title"
                data-testid="article-title"
              />
            </div>
            <div className="space-y-2">
              <Label>Slug (URL)</Label>
              <Input
                value={articleForm.slug}
                onChange={function(e) { setArticleForm(function(prev) { return { ...prev, slug: e.target.value }; }); }}
                placeholder="article-url-slug"
                data-testid="article-slug"
              />
            </div>
            <div className="space-y-2">
              <Label>Category</Label>
              <Select 
                value={articleForm.category}
                onValueChange={function(value) { setArticleForm(function(prev) { return { ...prev, category: value }; }); }}
              >
                <SelectTrigger data-testid="article-category">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {categories.map(function(cat) {
                    return <SelectItem key={cat} value={cat}>{cat}</SelectItem>;
                  })}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Excerpt (Short Description)</Label>
              <Textarea
                value={articleForm.excerpt}
                onChange={function(e) { setArticleForm(function(prev) { return { ...prev, excerpt: e.target.value }; }); }}
                placeholder="Brief description for article listings"
                rows={2}
                data-testid="article-excerpt"
              />
            </div>
            <div className="space-y-2">
              <Label>Featured Image URL (Optional)</Label>
              <Input
                value={articleForm.featured_image}
                onChange={function(e) { setArticleForm(function(prev) { return { ...prev, featured_image: e.target.value }; }); }}
                placeholder="https://..."
                data-testid="article-image"
              />
            </div>
            <div className="space-y-2">
              <Label>Content</Label>
              <Textarea
                value={articleForm.content}
                onChange={function(e) { setArticleForm(function(prev) { return { ...prev, content: e.target.value }; }); }}
                placeholder="Article content..."
                rows={10}
                data-testid="article-content"
              />
            </div>
            <div className="flex items-center gap-2">
              <Switch
                checked={articleForm.published}
                onCheckedChange={function(checked) { setArticleForm(function(prev) { return { ...prev, published: checked }; }); }}
                data-testid="article-published"
              />
              <Label>Published</Label>
            </div>
            <div className="flex justify-end gap-3 pt-4">
              <Button variant="outline" onClick={function() { setArticleDialogOpen(false); }}>
                Cancel
              </Button>
              <Button onClick={handleSaveArticle} className="btn-teal" disabled={savingArticle} data-testid="save-article-btn">
                {savingArticle ? 'Saving...' : 'Save Article'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
